/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { gql as apolloGql } from '@apollo/client';
import { Flex, Link, Text } from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { graphql as gatsbyGraphQL } from 'gatsby';
import React from 'react';
import { jsx } from 'theme-ui';
import { useFavorite } from '../../../plugins/gatsby-plugin-favorites/src';
import ToggleFavorite from '../../../plugins/gatsby-plugin-favorites/src/components/ToggleFavorite';
import { TastingNoteButton } from '../../../plugins/gatsby-plugin-tastingnotes/src';
import ListRow from '../List/ListRow';
import ListRowBody from '../List/ListRowBody';
import ProductFeatureIcon from './ProductFeatureIcon';
import ProductHeader, { ProductHeaderFragment } from '../Product/ProductHeader';
import ProductImage, { ProductImageFragment } from '../Product/ProductImage';
import useSiteConfig from '../useSiteConfig';
import ProductRequestSampleWithSnipcartButtonForList from './ProductRequestSampleWithSnipcartButtonForList';

/** @param {{ product: object; className?: string; [x: string]:any; }} props */
export default function ProductListRow({ product, className, ...rest }) {
  const favorite = useFavorite(
    product && { type: 'product', id: product.productId }
  );
  if (!product) return null;
  return (
    <ListRow sx={{ alignItems: 'stretch' }} className={className}>
      <Link
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          flexGrow: 1,
          color: 'inherit',
        }}
        {...rest}
      >
        <ProductListRowImage product={product} />
        <ListRowBody sx={{ position: 'relative', paddingRight: 5 }}>
          <ProductHeader product={product} />
        </ListRowBody>
        <ProductFeatures
          product={product}
          sx={{ marginRight: 2, marginBottom: 2 }}
        />
      </Link>
      {favorite.isLoading ? (
        <></>
      ) : (
        <ToggleFavorite
          isToggled={favorite.isSet}
          variant="small"
          onClick={() => favorite.toggle()}
          sx={{ position: 'absolute', top: 0, right: 0 }}
        />
      )}
      <ProductListRowActionsBar product={product} />
    </ListRow>
  );
}

export function ProductListRowImage({ product }) {
  return <ProductImage product={product} sx={{ margin: 3, marginRight: 0 }} />;
}

function ProductListRowActionsBar({ product, ...rest }) {
  const { sampleRequestsWithSnipcartEnabled, tastingNotesEnabled } =
    useSiteConfig();
  const { getButtonProps, getIconProps, tastingNote } = TastingNoteButton.use({
    productId: product.productId,
  });
  // TODO also check if logged in?
  if (!sampleRequestsWithSnipcartEnabled && !tastingNotesEnabled) return null;

  return (
    <Flex
      gap={2}
      wrap="wrap"
      align="center"
      sx={{
        width: '100%',
        backgroundColor: 'light',
        padding: 2,
        paddingBottom: 0,
      }}
      {...rest}
    >
      {tastingNote ? (
        <>
          <TastingNoteButton
            {...getButtonProps()}
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              marginBottom: 2,
            }}
            variant="text"
          >
            <TastingNoteButton.Icon {...getIconProps()} />
            {tastingNote.note ? (
              <Text
                variant="small"
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {tastingNote.note}
              </Text>
            ) : (
              <Text variant="small" sx={{ color: 'lighterText' }}>
                <Trans>(No tasting note)</Trans>
              </Text>
            )}
          </TastingNoteButton>
        </>
      ) : (
        <TastingNoteButton.Large
          productId={product.productId}
          sx={{ boxShadow: 'none', marginBottom: 2 }}
        />
      )}

      {sampleRequestsWithSnipcartEnabled && (
        <ProductRequestSampleWithSnipcartButtonForList
          product={product}
          sx={{ flexShrink: 0, marginLeft: 'auto', marginBottom: 2 }}
        />
      )}
    </Flex>
  );
}

export function ProductFeatures({ product, ...rest }) {
  if (!product?.features?.length) return null;
  return (
    <Flex
      sx={{
        width: '100%',
        paddingRight: 2,
        paddingBottom: 2,
        alignSelf: 'flex-end',
        position: 'relative', // Needed to stay above image backgrounds
        marginLeft: 'auto',
        justifyContent: 'flex-end',
      }}
      {...rest}
    >
      {product.features?.map((type) => (
        // Product features have to be queried separately by the parent.
        <ProductFeatureIcon
          key={type}
          variant="onPrimary"
          type={type}
          sx={{ marginTop: -3 }}
        />
      ))}
    </Flex>
  );
}

// HEADSUP: Product features have to be queried separately by the parent.

export const fragment = gatsbyGraphQL/* GraphQL */ `
  fragment bb_ProductListRow on Bottlebooks_Product {
    ...bb_ProductRequestSampleWithSnipcartButton
    ...bb_ProductHeader
  }
  fragment ProductListRow on Product {
    ...ProductImageLarge
    ...ProductHeader
  }

  # A fragment for the first page of products. It has more detailed images.
  fragment ProductListRow_first on Product {
    ...ProductImageLarge
  }
`;

ProductListRow.fragment = apolloGql/* GraphQL */ `
  fragment ProductListRow on Product {
    ...ProductImageLarge
    ...ProductHeader
  }
  ${ProductImageFragment}
  ${ProductHeaderFragment}
`;
